// Unit, density, thickness, and related conversion helpers

/**
 * Convert database unit format to UI unit format
 * @param dbUnit Unit as stored in database
 * @returns Unit as used in UI
 */
export const convertDbUnitToUIUnit = (dbUnit: string): string => {
  switch (dbUnit) {
    case "unit":
      return "Unidade";
    default:
      return dbUnit; // kg, L, m, t remain the same
  }
};

/**
 * Get the emission factor value for a given unit
 * Converts UI unit to database unit and retrieves the factor from the material doc.
 * For tons (t), automatically converts to kg and uses kg factor.
 * @param materialDoc Material document with new structure
 * @param unit Unit (UI format like "kg", "t", "m", "L", "Unidade")
 * @param factorType "carbon" or "hydric"
 * @returns Factor value (for tons, this returns the kg factor which will be applied to converted quantities)
 */
export const getFactorValue = (materialDoc: any, unit: string, factorType: "carbon" | "hydric"): number => {
  // Convert UI unit to database unit format
  const dbUnit = unit === "Unidade" ? "unit" : unit;

  const factors = factorType === "carbon" ? materialDoc.carbon_factors : materialDoc.hydric_factors;

  const extractFactorValue = (factorObj: any): number => {
    if (typeof factorObj === 'number') {
      return factorObj;
    } else if (factorObj && typeof factorObj === 'object' && 'value' in factorObj) {
      return factorObj.value || 0;
    } else if (factorObj && typeof factorObj === 'object' && 'factor' in factorObj) {
      return factorObj.factor || 0;
    }
    return 0;
  };

  // For tons, use kg factors since tons will be converted to kg during calculation
  if (dbUnit === "t") {
    const kgFactorObj = factors?.["kg"] || factors?.["fe_comb_kg"];
    const kgFactor = extractFactorValue(kgFactorObj);
    return kgFactor;
  }

  // Handle the actual database structure where factors are stored as direct values
  // For fuels: fe_comb_L, fe_comb_GJ, fh_comb_L, fh_comb_GJ
  // For materials: kg, L, m, unit, etc.
  let factor = 0;
  
  if (factorType === "carbon") {
    if (dbUnit === "L") {
      const factorObj = factors?.["fe_comb_L"] || factors?.["L"];
      factor = extractFactorValue(factorObj);
    } else if (dbUnit === "GJ") {
      const factorObj = factors?.["fe_comb_GJ"] || factors?.["GJ"];
      factor = extractFactorValue(factorObj);
    } else if (dbUnit === "unit") {
      // For materials with "Unidade" units, check for unit-specific factors
      const factorObj = factors?.["unit"] || factors?.["fe_comb_unit"] || factors?.["per_unit"];
      factor = extractFactorValue(factorObj);
    } else {
      const factorObj = factors?.[dbUnit];
      factor = extractFactorValue(factorObj);
    }
  } else if (factorType === "hydric") {
    if (dbUnit === "L") {
      const factorObj = factors?.["fh_comb_L"] || factors?.["L"];
      factor = extractFactorValue(factorObj);
    } else if (dbUnit === "GJ") {
      const factorObj = factors?.["fh_comb_GJ"] || factors?.["GJ"];
      factor = extractFactorValue(factorObj);
    } else if (dbUnit === "unit") {
      // For materials with "Unidade" units, check for unit-specific factors
      const factorObj = factors?.["unit"] || factors?.["fh_comb_unit"] || factors?.["per_unit"];
      factor = extractFactorValue(factorObj);
    } else {
      const factorObj = factors?.[dbUnit];
      factor = extractFactorValue(factorObj);
    }
  } else {
    const factorObj = factors?.[dbUnit];
    factor = extractFactorValue(factorObj);
  }

  return factor;
};

/**
 * Convert quantity and unit to kilograms for consistent calculation
 * This ensures tons are properly converted to kg for calculations
 * @param quantity Original quantity
 * @param unit Original unit (UI format)
 * @returns Object with converted quantity and unit
 */
export const convertToKgForCalculation = (quantity: number, unit: string): { quantity: number; unit: string } => {
  if (unit === "t") {
    return {
      quantity: quantity * 1000, // Convert tons to kg
      unit: "kg",
    };
  }
  return { quantity, unit };
};
